import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./MailHistory.scss";

interface IProps {
  mailHistory: any;
}

const MailHistory: React.FC<IProps> = ({ mailHistory }) => {
  const [showAll, setShowAll] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const navigate = useNavigate();

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getShortDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();

    // Compare calendar dates only (ignore time component)
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffTime = nowOnly.getTime() - dateOnly.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const getIconClasses = (channel: any) => {
    switch (channel) {
      case "Email":
      case "email":
        return "fa fa-envelope fs-6 text-white";
      case "outbound_phone":
      case "inbound_phone":
        return "bi bi-telephone fs-6 text-white";
      case "Chat":
        return "bi bi-chat-left-dots fs-6 text-dark";
      case "Whatsapp":
        return "bi bi-whatsapp fs-4 text-success";
      case "app":
        return "bi bi-google-play fs-6 text-white";
      case "Social-Media":
        return "bi bi-dribbble fs-6 text-white";
      case "Facebook":
        return "bi bi-facebook fs-6 text-white";
      case "Walk-in":
        return "bi bi-person-walking fs-6 text-white";
      default:
        return "fa fa-envelope fs-6 text-white";
    }
  };

  const getBadgeClasses = (channel: any) => {
    switch (channel) {
      case "Email":
      case "email":
        return "bg-warning rounded-circle";
      case "outbound_phone":
        return "bg-success rounded-circle";
      case "inbound_phone":
        return "bg-danger rounded-circle";
      case "Chat":
        return "bg-white border border-dark rounded-circle";
      case "Whatsapp":
        return "bg-success rounded-circle";
      case "app":
        return "bg-success rounded-circle";
      case "Social-Media":
      case "Facebook":
        return "bg-primary rounded-circle";
      case "Walk-in":
        return "bg-info rounded-circle";
      default:
        return "bg-warning rounded-circle";
    }
  };

  const displayedData = showAll ? mailHistory : mailHistory?.slice(0, 5);

  if (!mailHistory || mailHistory.length === 0) {
    return (
      <div className="history-empty-state">
        <div className="empty-icon-circle">
          <i className="bi bi-clock-history"></i>
        </div>
        <p className="empty-text">No history available</p>
      </div>
    );
  }

  return (
    <div className="mail-history-modern">
      <div className="history-timeline">
        {displayedData?.map((item: any, index: number) => (
          <div
            key={index}
            className={`timeline-item ${
              hoveredIndex === index ? "hovered" : ""
            }`}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            onClick={() =>
              navigate(
                `/ticket/information/${
                  item?.ticket?.id ? item?.ticket?.id : item?.ref_id
                }`,
              )
            }
          >
            {/* Timeline Track */}
            <div className="timeline-track">
              <div
                className={`timeline-dot ${getBadgeClasses(
                  item?.ticket?.ticket_channel,
                )}`}
              >
                <i className={getIconClasses(item?.ticket?.ticket_channel)}></i>
              </div>
              {index !== displayedData.length - 1 && (
                <div className="timeline-line"></div>
              )}
            </div>

            {/* Content Card */}
            <div className="timeline-card">
              {/* Quick Info Bar */}
              <div className="card-quick-info">
                <div className="info-left">
                  <span className="channel-type">
                    {item?.ticket?.ticket_channel || "Email"}
                  </span>
                  <span className="separator">•</span>
                  <span className="time-short">
                    {getShortDate(
                      item?.ticket?.created ||
                        item?.created ||
                        new Date().toISOString(),
                    )}
                  </span>
                </div>
                <div className="info-right">
                  <span className="ticket-id">
                    #{item?.ticket?.id || item?.ref_id}
                  </span>
                  <i className="bi bi-chevron-right arrow-icon"></i>
                </div>
              </div>

              {/* Main Content - Only shows on hover or mobile */}
              <div className="card-expandable-content">
                {item.message && (
                  <div className="content-message">
                    <p>{item.message}</p>
                  </div>
                )}

                {item?.ticket?.description && (
                  <div className="content-description">
                    <p>{item.ticket.description}</p>
                  </div>
                )}

                <div className="content-footer">
                  <div className="footer-item">
                    <i className="bi bi-calendar3"></i>
                    <span>
                      {formatDate(
                        item?.ticket?.created ||
                          item?.created ||
                          new Date().toISOString(),
                      )}
                    </span>
                  </div>
                  {item?.assignee?.username && (
                    <div className="footer-item assignee">
                      <i className="bi bi-person"></i>
                      <span>{item.assignee.username}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Show More */}
      {mailHistory?.length > 5 && (
        <div className="history-show-more">
          <button
            className="show-more-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowAll(!showAll);
            }}
          >
            <i
              className={`bi ${showAll ? "bi-chevron-up" : "bi-chevron-down"}`}
            ></i>
            <span>
              {showAll ? "Show Less" : `Show ${mailHistory.length - 5} More`}
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default MailHistory;